import React, { useState } from 'react';
import { 
  X, 
  Calendar, 
  Camera, 
  FileText, 
  Trash2,
  CheckCircle2
} from 'lucide-react';
import { useLang } from '../../core/lang';
import LangKeys from '../../core/locales/keys';
import { addEntry, updateEntry, addCashbookEntry } from '../../services/dashboardApi';
import CalculatorKeyboard from '../shared/CalculatorKeyboard';
import { compressImage } from '../../core/imageUtils';

const AddEntryModal = ({ party, type, editEntry, onClose, onSuccess, isSupplier }) => {
  const { t } = useLang();
  const [currentType, setCurrentType] = useState(editEntry?.entry_type || type || 'get');
  const [amount, setAmount] = useState(editEntry?.amount || '');
  const [note, setNote] = useState(editEntry?.note || '');
  
  const now = new Date();
  const initialDate = editEntry?.entry_date ? editEntry.entry_date.split(' ')[0] : now.toISOString().split('T')[0];
  const initialTime = editEntry?.entry_date && editEntry.entry_date.includes(' ') 
    ? editEntry.entry_date.split(' ')[1].substring(0, 5) 
    : now.getHours().toString().padStart(2, '0') + ':' + now.getMinutes().toString().padStart(2, '0');

  const [date, setDate] = useState(initialDate);
  const [time, setTime] = useState(initialTime);
  const [image, setImage] = useState(null);
  const [preview, setPreview] = useState(editEntry?.entry_image ? (editEntry.entry_image.startsWith('http') ? editEntry.entry_image : `https://khata.moxpal.com/${editEntry.entry_image}`) : null);
  const [addToCashbook, setAddToCashbook] = useState(false);
  const [paymentMode, setPaymentMode] = useState('cash'); 
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);

  // CALCULATOR
  const [showKeyboard, setShowKeyboard] = useState(false);
  const [expression, setExpression] = useState(editEntry?.amount?.toString() || '');
  const [realTimeResult, setRealTimeResult] = useState('');

  const calculateRealTime = (expr) => {
    if (!expr || ['+', '-', '×', '÷', '%', '.'].includes(expr.slice(-1))) { setRealTimeResult(''); return; }
    try {
      const finalExpr = expr.replace(/×/g, '*').replace(/÷/g, '/');
      const evalResult = Function('"use strict";return (' + finalExpr + ')')();
      if (!isNaN(evalResult)) setRealTimeResult(`${expr} = ${Number.isInteger(evalResult) ? evalResult : evalResult.toFixed(2)}`);
    } catch (e) { setRealTimeResult(''); }
  };

  const handleKeyTap = (key) => {
    let newExpr = expression;
    if (key === '=') { if (realTimeResult.includes('=')) newExpr = realTimeResult.split(' = ')[1]; setRealTimeResult(''); }
    else if (key === 'C') { newExpr = ''; setRealTimeResult(''); }
    else if (key === '⌫') newExpr = expression.slice(0, -1);
    else {
      if (['+', '-', '×', '÷', '.'].includes(key) && expression.length > 0 && ['+', '-', '×', '÷', '%', '.'].includes(expression.slice(-1))) return;
      newExpr += key;
    }
    setExpression(newExpr); setAmount(newExpr); calculateRealTime(newExpr);
  };

  const handleImageChange = async (e) => {
    const file = e.target.files[0];
    if (file) {
      try {
        const compressed = await compressImage(file);
        setImage(compressed);
        const reader = new FileReader();
        reader.onloadend = () => setPreview(reader.result);
        reader.readAsDataURL(compressed);
      } catch (err) {
        console.error("Compression failed:", err);
        setImage(file);
        const reader = new FileReader();
        reader.onloadend = () => setPreview(reader.result);
        reader.readAsDataURL(file);
      }
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!amount || parseFloat(amount) <= 0) return;

    try {
      setLoading(true);
      const isoDate = `${date} ${time}:00`;
      
      const partyFormData = new FormData();
      if (editEntry) partyFormData.append('id', editEntry.id.toString());
      partyFormData.append('customer_id', party.id.toString());
      partyFormData.append('entry_type', currentType);
      partyFormData.append('amount', amount.toString());
      partyFormData.append('note', note);
      partyFormData.append('entry_date', isoDate);
      partyFormData.append('date', isoDate);
      partyFormData.append('time', `${time}:00`);
      if (image) partyFormData.append('entry_image', image);

      const res = editEntry 
        ? await updateEntry(editEntry.id, partyFormData)
        : await addEntry(partyFormData);

      if (res?.success) {
          if (addToCashbook && !editEntry) {
              const cbFormData = new FormData();
              const cbType = currentType === 'get' ? 'in' : 'out';
              const cbNote = note.trim() ? `${party.name} - ${note}` : party.name;
              cbFormData.append('entry_type', cbType);
              cbFormData.append('amount', amount.toString());
              cbFormData.append('payment_mode', paymentMode);
              cbFormData.append('note', cbNote);
              cbFormData.append('entry_date', isoDate);
              cbFormData.append('transaction_id', crypto.randomUUID());
              if (image) cbFormData.append('bill_image', image);
              try { await addCashbookEntry(cbFormData); } catch (cbErr) { console.error("Cashbook sync failed", cbErr); }
          }
          setSuccess(true);
          setTimeout(() => { onSuccess(); onClose(); }, 1500);
      } else {
        alert(res?.message || 'Failed to save entry');
      }
    } catch (error) {
      console.error('Submit error:', error);
      alert('Error saving entry.');
    } finally {
      setLoading(false);
    }
  };

  if (success) {
    return (
      <div style={styles.overlay} onClick={onClose}>
        <div style={styles.successCard} onClick={e => e.stopPropagation()}>
          <div style={styles.successIcon}><CheckCircle2 size={64} color="#10B981" /></div>
          {/* successTitle removed as per user request */}
          <p style={styles.successText}>₹{parseFloat(amount).toLocaleString()} {currentType === 'get' ? t(LangKeys.received) : t(LangKeys.paid)}</p>
        </div>
      </div>
    );
  }

  const activeColor = currentType === 'get' ? '#10B981' : '#EF4444';

  return (
    <div style={styles.overlay} onClick={onClose}>
      <div style={styles.modal} onClick={e => e.stopPropagation()}>
        <div style={styles.header}>
          <h3 style={styles.title}>{editEntry ? t(LangKeys.edit_entry) : t(LangKeys.add_entry_title)}</h3>
          <button style={styles.closeBtn} onClick={onClose}><X size={24} /></button>
        </div>

        <form onSubmit={handleSubmit} style={styles.form}>
          <div style={styles.typeToggle}>
            <button type="button" style={{...styles.toggleBtn, ...(currentType === 'get' ? { background: '#F0FDF4', color: '#10B981', borderColor: '#10B981' } : {})}} onClick={() => setCurrentType('get')}>
              {isSupplier ? t(LangKeys.received) : t(LangKeys.i_got)}
            </button>
            <button type="button" style={{...styles.toggleBtn, ...(currentType === 'give' ? { background: '#FFF1F2', color: '#EF4444', borderColor: '#EF4444' } : {})}} onClick={() => setCurrentType('give')}>
              {isSupplier ? t(LangKeys.paid) : t(LangKeys.i_gave)}
            </button>
          </div>

          <div style={styles.inputGrid}>
            <div style={styles.inputGroup}>
              <label style={styles.label}>{t(LangKeys.amount)}</label>
              <div style={styles.amountInputWrap}>
                <span style={{...styles.currency, color: activeColor}}>₹</span>
                <input type="text" readOnly style={{...styles.amountInput, color: activeColor}} value={expression} onFocus={() => setShowKeyboard(true)} />
              </div>
              {realTimeResult && <div style={styles.realTimeResult}>{realTimeResult}</div>}
            </div>

            {showKeyboard && <CalculatorKeyboard onKeyTap={handleKeyTap} onHide={() => setShowKeyboard(false)} onMemoryClear={() => {}} />}

            <div style={styles.row}>
              <div style={{...styles.inputGroup, flex: 1}}>
                <label style={styles.label}>{t(LangKeys.date)}</label>
                <input type="date" style={styles.inputBox} value={date} onChange={(e) => setDate(e.target.value)} />
              </div>
              <div style={{...styles.inputGroup, width: '120px'}}>
                <label style={styles.label}>Time</label>
                <input type="time" style={styles.inputBox} value={time} onChange={(e) => setTime(e.target.value)} />
              </div>
            </div>

            <div style={styles.inputGroup}>
              <label style={styles.label}>{t(LangKeys.note)}</label>
              <input type="text" placeholder={t(LangKeys.note_hint)} style={styles.inputBox} value={note} onChange={(e) => setNote(e.target.value)} />
            </div>

            <div style={styles.photoSection}>
              {!preview ? (
                <label style={styles.photoUpload}>
                  <Camera size={24} color="#64748B" />
                  <span>{t(LangKeys.add_photo)}</span>
                  <input type="file" accept="image/*" hidden onChange={handleImageChange} />
                </label>
              ) : (
                <div style={styles.previewWrap}>
                  <img src={preview} alt="Preview" style={styles.preview} />
                  <button type="button" style={styles.removeImg} onClick={() => {setImage(null); setPreview(null);}}><Trash2 size={16} /></button>
                </div>
              )}
            </div>

            <div style={styles.cashbookSection}>
              <label style={styles.checkboxLabel}>
                <input type="checkbox" checked={addToCashbook} onChange={(e) => setAddToCashbook(e.target.checked)} style={styles.checkboxInput} />
                <span style={styles.checkboxText}>{t(LangKeys.add_to_cashbook)}</span>
              </label>
              
              {addToCashbook && (
                <div style={styles.paymentModes}>
                  <button type="button" style={{...styles.modeBtn, ...(paymentMode === 'cash' ? styles.activeMode : {})}} onClick={() => setPaymentMode('cash')}>{t(LangKeys.cash)}</button>
                  <button type="button" style={{...styles.modeBtn, ...(paymentMode === 'online' ? styles.activeMode : {})}} onClick={() => setPaymentMode('online')}>{t(LangKeys.online)}</button>
                </div>
              )}
            </div>
          </div>

          <button type="submit" style={{...styles.submitBtn, background: activeColor}} disabled={loading}>
            {loading ? '...' : (editEntry ? t(LangKeys.update_entry) : t(LangKeys.save_entry).toUpperCase())}
          </button>
        </form>
      </div>
    </div>
  );
};

const styles = {
  overlay: { position: 'fixed', inset: 0, background: 'rgba(15, 23, 42, 0.4)', backdropFilter: 'blur(8px)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 2000, padding: '20px' },
  modal: { background: '#FFFFFF', borderRadius: '28px', width: '100%', maxWidth: '400px', boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25)', overflow: 'hidden' },
  header: { padding: '20px 24px', borderBottom: '1px solid #F1F5F9', display: 'flex', alignItems: 'center', justifyContent: 'space-between' },
  title: { fontSize: '18px', fontWeight: '800', color: '#0F172A', margin: 0 },
  closeBtn: { border: 'none', background: 'transparent', cursor: 'pointer', color: '#94A3B8', padding: '4px', display: 'flex' },
  form: { padding: '20px', display: 'flex', flexDirection: 'column', gap: '20px' },
  typeToggle: { display: 'flex', gap: '8px', background: '#F8FAFC', padding: '4px', borderRadius: '12px' },
  toggleBtn: { flex: 1, padding: '10px', borderRadius: '10px', border: '1px solid transparent', background: 'transparent', fontWeight: '700', fontSize: '14px', cursor: 'pointer' },
  inputGrid: { display: 'flex', flexDirection: 'column', gap: '16px' },
  inputGroup: { display: 'flex', flexDirection: 'column', gap: '6px' },
  label: { fontSize: '11px', fontWeight: '700', color: '#94A3B8', textTransform: 'uppercase' },
  amountInputWrap: { display: 'flex', alignItems: 'center', gap: '10px', borderBottom: '2px solid #F1F5F9' },
  currency: { fontSize: '28px', fontWeight: '900' },
  amountInput: { flex: 1, border: 'none', background: 'transparent', outline: 'none', fontSize: '32px', fontWeight: '900' },
  row: { display: 'flex', gap: '12px' },
  inputBox: { width: '100%', height: '44px', background: '#F8FAFC', border: '1px solid #F1F5F9', borderRadius: '12px', padding: '0 12px', fontSize: '14px', outline: 'none' },
  photoSection: { marginTop: '4px' },
  photoUpload: { display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '6px', padding: '16px', background: '#F8FAFC', border: '2px dashed #E2E8F0', borderRadius: '14px', cursor: 'pointer', color: '#64748B', fontSize: '13px', fontWeight: '600' },
  previewWrap: { position: 'relative', width: '80px', height: '80px', borderRadius: '12px', overflow: 'hidden' },
  preview: { width: '100%', height: '100%', objectFit: 'cover' },
  removeImg: { position: 'absolute', top: '4px', right: '4px', width: '20px', height: '20px', borderRadius: '10px', background: 'rgba(239, 68, 68, 0.9)', color: '#FFFFFF', border: 'none', display: 'flex', alignItems: 'center', justifyContent: 'center' },
  cashbookSection: { background: '#F8FAFC', padding: '12px', borderRadius: '14px', border: '1px solid #F1F5F9', display: 'flex', flexDirection: 'column', gap: '10px' },
  checkboxLabel: { display: 'flex', alignItems: 'center', gap: '10px', cursor: 'pointer' },
  checkboxInput: { width: '18px', height: '18px', cursor: 'pointer' },
  checkboxText: { fontSize: '14px', fontWeight: '700', color: '#1E293B' },
  paymentModes: { display: 'flex', gap: '8px' },
  modeBtn: { flex: 1, padding: '8px', borderRadius: '10px', border: '1px solid #E2E8F0', background: '#FFFFFF', fontSize: '12px', fontWeight: '700', color: '#64748B', cursor: 'pointer' },
  activeMode: { borderColor: '#3B82F6', color: '#3B82F6', background: '#EFF6FF' },
  submitBtn: { marginTop: '8px', height: '52px', borderRadius: '14px', border: 'none', color: '#FFFFFF', fontSize: '16px', fontWeight: '800', cursor: 'pointer', boxShadow: '0 10px 25px rgba(0, 0, 0, 0.1)' },
  successCard: { background: '#FFFFFF', borderRadius: '24px', padding: '30px', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '12px', textAlign: 'center' },
  successText: { fontSize: '18px', fontWeight: '700', color: '#64748B', margin: 0 },
  realTimeResult: { fontSize: '13px', color: '#64748B', textAlign: 'right', fontWeight: '600' }
};

export default AddEntryModal;
